import {ReactNode} from 'react';
import {span} from '@xh/hoist/cmp/layout';
import {fmtNumber, fmtPercent, Precision} from '@xh/hoist/format';
import {moneyFormatter} from '../../../../cmp/Formaters';
import {Dir, signDir, signedMoney} from '../../../../cmp/kpi/Kpi';
import {RiskAnalytics} from '../../../../data/DataTypes';
import {ChartKind} from './PerformanceCharts';
import {PerformanceWidgetModel} from './PerformanceWidgetModel';

/**
 * The rows of the Performance widget - performance on the left, risk on the right, the same number of each so the
 * two columns line up. Each row names the chart shown when it's the active metric.
 */
export interface MetricDef {
    id: string;
    label: string;
    title: string;
    chart: ChartKind;
    value: (src: MetricSources) => MetricValue;
}

export interface MetricSources {
    figures: PerformanceWidgetModel;
    risk: RiskAnalytics;
}

export interface MetricValue {
    text: ReactNode;
    dir?: Dir;
}

/**
 * How a risk value rates against the usual rules of thumb - good, fair or poor. The ranges are in each row's tooltip.
 * They are general guides for a diversified portfolio, not targets.
 */
export type Rating = 'good' | 'fair' | 'poor';

export const RATING_CLASS: Record<Rating, string> = {good: 'rzx-dir--up', fair: 'rzx-amber', poor: 'rzx-dir--down'};

/** Higher is better: poor below `poor`, good from `good` up, fair between. */
function higher(v: number, poor: number, good: number): Rating {
    return v == null ? null : v < poor ? 'poor' : v >= good ? 'good' : 'fair';
}

/** Lower is better: good up to `good`, poor above `poor`, fair between. */
function lower(v: number, good: number, poor: number): Rating {
    return v == null ? null : v <= good ? 'good' : v > poor ? 'poor' : 'fair';
}

/** A value colored by its rating. */
function rated(text: string, rating: Rating): ReactNode {
    return rating ? span({className: RATING_CLASS[rating], item: text}) : text;
}

/** Two values on one row, each colored by its own rating. */
function ratedPair(a: string, aRating: Rating, b: string, bRating: Rating): MetricValue {
    return a === NONE && b === NONE ? {text: NONE} : {text: span({items: [rated(a, aRating), ' / ', rated(b, bRating)]})};
}

const NONE = '-';

export const PERFORMANCE_METRICS: MetricDef[] = [
    {
        id: 'mv',
        label: 'MV',
        chart: 'balance',
        title: 'Market value of the positions, cash portfolios excluded',
        value: ({figures}) => ({text: moneyFormatter(figures.mv)})
    },
    {
        id: 'dly',
        label: 'DLY P&L',
        chart: 'dailyPnl',
        title: 'Change in market value since the last close',
        value: ({figures}) => ({text: signedMoney(figures.dly), dir: signDir(figures.dly)})
    },
    {
        id: 'dlyPct',
        label: 'DLY %',
        chart: 'dailyReturns',
        title: 'DLY P&L as a share of the market value at the last close',
        value: ({figures}) => {
            const prev = figures.mv - figures.dly,
                v = prev ? figures.dly / Math.abs(prev) : null;
            return signedPctValue(v);
        }
    },
    {
        id: 'ytd',
        label: 'YTD P&L',
        chart: 'ytdPnl',
        title: 'Change in market value since the end of last year, net of deposits',
        value: ({figures}) => ({text: signedMoney(figures.ytd), dir: signDir(figures.ytd)})
    },
    {
        id: 'ytdPct',
        label: 'YTD %',
        chart: 'ytdGrowth',
        title: 'YTD P&L as a share of the market value at the end of last year',
        value: ({figures}) => signedPctValue(figures.ytdPct)
    },
    {
        id: 'ytdDva',
        label: 'YTD DVA',
        chart: 'ytdBalance',
        title: 'Change in market value since the end of last year, deposits included',
        value: ({figures}) => ({text: signedMoney(figures.ytdDva), dir: signDir(figures.ytdDva)})
    },
    {
        id: 'ytdDvaPct',
        label: 'YTD DVA %',
        chart: 'ytdDva',
        title: 'YTD DVA as a share of the market value at the end of last year',
        value: ({figures}) => signedPctValue(figures.ytdDvaPct)
    },
    {
        id: 'deposits',
        label: 'YTD Deposits',
        chart: 'flows',
        title: 'Net deposits since the start of the year - withdrawals count against them',
        value: ({figures}) => ({text: signedMoney(figures.calcData?.currentYearDeposits)})
    },
    {
        id: 'oneYear',
        label: '1Y Return',
        chart: 'oneYear',
        title: 'Compounded daily return over the last year, deposits excluded',
        value: ({risk}) => signedPctValue(risk?.oneYearReturn)
    },
    {
        id: 'cagr',
        label: 'CAGR',
        chart: 'cagr',
        title: 'Compound annual growth rate since the first balance',
        value: ({figures}) => signedPctValue(figures.cagr)
    },
    {
        id: 'alpha',
        label: 'Alpha (ann.)',
        chart: 'alpha',
        title: "Jensen's alpha, r - (Rf + β × (Rm - Rf)) - the yearly return beyond what beta to SPX explains. The daily average over the window, annualized",
        value: ({risk}) => signedPctValue(risk?.alpha)
    },
    {
        id: 'turnover',
        label: 'Turnover',
        chart: 'trades',
        title: 'The smaller of buys and sells over the average balance, per year, over the window',
        value: ({risk}) => ({text: pctText(risk?.turnover, 1)})
    },
    {
        id: 'bestWorst',
        label: 'Best / Worst Day',
        chart: 'bestWorst',
        title: 'The best and worst daily return this year',
        value: ({risk}) => ({
            text: pairText(pctText(risk?.bestDayYtd?.return, 2, true), pctText(risk?.worstDayYtd?.return, 2, true))
        })
    },
    {
        id: 'dividends',
        label: 'Dividends',
        chart: 'dividends',
        title: 'Sum of the last dividend of each position',
        value: ({figures}) => ({text: moneyFormatter(figures.dividends)})
    },
    {
        id: 'swr',
        label: 'SWR',
        chart: 'swr',
        title: 'Safe withdrawal rate - 4% of market value',
        value: ({figures}) => ({text: moneyFormatter(figures.swr)})
    },
    {
        id: 'rmd',
        label: 'RMD',
        chart: 'rmd',
        title: 'Required minimum distribution estimate - 4% of tax-deferred market value',
        value: ({figures}) => ({text: moneyFormatter(figures.rmd)})
    }
];

export const RISK_METRICS: MetricDef[] = [
    {
        id: 'beta',
        label: 'Beta to SPX',
        chart: 'beta',
        title: 'Slope of the daily returns against SPX, over the window',
        value: ({risk}) => ({text: numText(risk?.beta)})
    },
    {
        id: 'correlation',
        label: 'Correlation to SPX',
        chart: 'rollingCorrelation',
        title: 'Correlation of the daily returns with SPX',
        value: ({risk}) => ({text: numText(risk?.correlation)})
    },
    {
        id: 'volatility',
        label: 'Volatility (ann.)',
        chart: 'rollingVolatility',
        title: 'Standard deviation of the daily returns, annualized. Under 15% calm, 15% to 25% equity-like, over 25% high',
        value: ({risk}) => ({text: rated(pctText(risk?.volatility, 1), lower(risk?.volatility, 0.15, 0.25))})
    },
    {
        id: 'sharpe',
        label: 'Sharpe',
        chart: 'rollingSharpe',
        title: 'Return above the T-bill rate per unit of volatility, annualized. 1 or more good, 0 to 1 suboptimal, below 0 poor',
        value: ({risk}) => ({text: rated(numText(risk?.sharpe), higher(risk?.sharpe, 0, 1))})
    },
    {
        id: 'sortino',
        label: 'Sortino',
        chart: 'rollingSortino',
        title: 'Like Sharpe, but only falls below the T-bill rate count as risk. 2 or more good, 0 to 2 fair, below 0 poor',
        value: ({risk}) => ({text: rated(numText(risk?.sortino), higher(risk?.sortino, 0, 2))})
    },
    {
        id: 'omega',
        label: 'Omega',
        chart: 'omega',
        title: 'Sum of the daily gains over the sum of the daily losses. 1.2 or more good, 1 to 1.2 fair, below 1 the losses outweigh the gains',
        value: ({risk}) => ({text: rated(numText(risk?.omega), higher(risk?.omega, 1, 1.2))})
    },
    {
        id: 'calmar',
        label: 'Calmar',
        chart: 'calendarYears',
        title: 'Annualized return over the maximum drawdown. 1 or more good, 0.5 to 1 fair, below 0.5 poor',
        value: ({risk}) => ({text: rated(numText(risk?.calmar), higher(risk?.calmar, 0.5, 1))})
    },
    {
        id: 'maxDrawdown',
        label: 'Max Drawdown',
        chart: 'drawdown',
        title: 'Deepest fall below a previous high, over the window. Within 10% good, 10% to 20% fair, deeper than 20% poor',
        value: ({risk}) => ({text: rated(pctText(risk?.maxDrawdown, 1), higher(risk?.maxDrawdown, -0.2, -0.1))})
    },
    {
        id: 'ulcer',
        label: 'Ulcer Index',
        chart: 'rollingUlcer',
        title: 'Root mean square of the drawdowns, in percent points - depth and duration together. Up to 5 good, 5 to 10 fair, over 10 poor',
        value: ({risk}) => ({text: rated(numText(risk?.ulcerIndex), lower(risk?.ulcerIndex, 5, 10))})
    },
    {
        id: 'var',
        label: 'VaR 95% (1d)',
        chart: 'histogram',
        title: "The daily loss exceeded on 5% of days, and what it is on today's market value. Within 1.5% good, 1.5% to 2.5% fair, over 2.5% poor",
        value: src => tailValue(src, src.risk?.var95, higher(src.risk?.var95, -0.025, -0.015))
    },
    {
        id: 'cvar',
        label: 'CVaR 95% (1d)',
        chart: 'tail',
        title: 'The average loss on the worst 5% of days. Within 2% good, 2% to 3.5% fair, over 3.5% poor',
        value: src => tailValue(src, src.risk?.cvar95, higher(src.risk?.cvar95, -0.035, -0.02))
    },
    {
        id: 'moments',
        label: 'Skew / Kurtosis',
        chart: 'qq',
        title: 'Skewness - negative means a longer tail of losses: 0 or more good, down to -0.5 fair, below that poor. Excess kurtosis - positive means fatter tails than normal: up to 1 good, 1 to 3 fair, over 3 poor',
        value: ({risk}) => ratedPair(numText(risk?.skewness), higher(risk?.skewness, -0.5, 0), numText(risk?.kurtosis), lower(risk?.kurtosis, 1, 3))
    },
    {
        id: 'capture',
        label: 'Up / Down Capture',
        chart: 'capture',
        title: "Average return on SPX up days, and on its down days, as a share of SPX's own. Up: 100% or more good, 80% to 100% fair, below 80% poor. Down: up to 90% good, 90% to 110% fair, over 110% poor",
        value: ({risk}) =>
            ratedPair(pctText(risk?.upCapture, 0), higher(risk?.upCapture, 0.8, 1), pctText(risk?.downCapture, 0), lower(risk?.downCapture, 0.9, 1.1))
    },
    {
        id: 'tracking',
        label: 'TE / Info Ratio',
        chart: 'activeReturn',
        title: 'Tracking error - volatility of the return ahead of SPX - and the information ratio, that return per unit of it. Information ratio: 0.5 or more good, 0 to 0.5 fair, below 0 poor',
        value: ({risk}) => ratedPair(pctText(risk?.trackingError, 1), null, numText(risk?.informationRatio), higher(risk?.informationRatio, 0, 0.5))
    },
    {
        id: 'hhi',
        label: 'Concentration',
        chart: 'weights',
        title: 'Herfindahl index of the holding weights, and the effective number of equal holdings it amounts to. Up to 0.15 diversified, 0.15 to 0.25 moderately concentrated, over 0.25 concentrated',
        value: ({risk}) => ({
            text: rated(
                risk?.hhi != null ? `${fmtNumber(risk.hhi, {precision: 3})} (N ${fmtNumber(risk.effectiveN, {precision: 0})})` : NONE,
                lower(risk?.hhi, 0.15, 0.25)
            )
        })
    },
    {
        id: 'liquidity',
        label: 'Days to Liquidate',
        chart: 'liquidity',
        title: 'Days to sell every holding at 20% of its average daily volume - the slowest holding sets it. Up to 1 good, 1 to 5 fair, over 5 poor',
        value: ({risk}) => ({
            text: rated(risk?.daysToLiquidate != null ? (fmtNumber(risk.daysToLiquidate, {precision: 1}) as string) : NONE, lower(risk?.daysToLiquidate, 1, 5))
        })
    }
];

export const ALL_METRICS = [...PERFORMANCE_METRICS, ...RISK_METRICS];

function signedPctValue(v: number): MetricValue {
    return {text: pctText(v, 2, true), dir: v != null ? signDir(v) : null};
}

function pctText(v: number, precision: Precision, withPlusSign = false): string {
    return v != null ? (fmtPercent(v, {precision, withPlusSign}) as string) : NONE;
}

function numText(v: number): string {
    return v != null ? (fmtNumber(v, {precision: 2}) as string) : NONE;
}

function pairText(a: string, b: string): string {
    return a === NONE && b === NONE ? NONE : `${a} / ${b}`;
}

/** A tail loss as a percent, and in dollars on today's market value. */
function tailValue({figures}: MetricSources, v: number, rating: Rating): MetricValue {
    if (v == null) return {text: NONE};
    const dollars = Math.abs(v * figures.mv),
        compact = dollars >= 1e6 ? `$${(dollars / 1e6).toFixed(1)}m` : `$${(dollars / 1e3).toFixed(0)}k`;
    return {text: rated(`${pctText(v, 2)} / ${compact}`, rating)};
}
